import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useLanguage } from '../context/LanguageContext';
import StudentHeader from '../components/StudentHeader';
import { studentFetch } from '../utils/authFetch';

const API = process.env.REACT_APP_API_URL || 'http://localhost:8000';

const MOCK_LEADERBOARD = [
  { rank: 1, name: 'Ahmad',  avatar: 'A', avatarColor: 'blue',   score: 980, isMe: false },
  { rank: 2, name: 'Sara',   avatar: 'S', avatarColor: 'purple', score: 940, isMe: false },
  { rank: 3, name: 'Mariam', avatar: 'M', avatarColor: 'amber',  score: 905, isMe: false },
  { rank: 4, name: 'Khalid', avatar: 'K', avatarColor: 'teal',   score: 870, isMe: false },
  { rank: 5, name: 'Nour',   avatar: 'N', avatarColor: 'red',    score: 840, isMe: false },
  { rank: 12, name: 'Jana (أنت)', avatar: 'J', avatarColor: 'me', score: 620, isMe: true },
];

const MOCK_STATS = { activeCount: 4, totalXp: 1240, streakDays: 7 };

// ─── THEME TOKENS ────────────────────────────────────────────────────────────
const T = {
  navy:    '#1E3A5F',
  em:      '#059669',
  em2:     '#10B981',
  emBg:    '#ECFDF5',
  emBord:  'rgba(5,150,105,.2)',
  gold:    '#D97706',
  gold2:   '#F59E0B',
  goldBg:  '#FFFBEB',
  red:     '#DC2626',
  purple:  '#7C3AED',
  cardRad: '22px',
  shadow:  '0 5px 0 #E5E7EB, 0 10px 28px rgba(0,0,0,.07)',
};

const AVATAR_COLORS = {
  blue:   { bg: '#DBEAFE', fg: '#1E40AF' },
  purple: { bg: '#EDE9FE', fg: '#5B21B6' },
  amber:  { bg: '#FEF3C7', fg: '#92400E' },
  teal:   { bg: '#CCFBF1', fg: '#0F766E' },
  red:    { bg: '#FEE2E2', fg: '#991B1B' },
  me:     { bg: T.em,      fg: '#ffffff' },
};

const CARD_ACCENT = {
  weekly:  { border: T.red,    iconBg: '#FEE2E2', bar: '#EF4444', btnBg: '#FEE2E2', btnFg: '#991B1B' },
  improve: { border: T.em,     iconBg: T.emBg,   bar: T.em2,     btnBg: T.emBg,    btnFg: '#065F46' },
  daily:   { border: T.gold,   iconBg: T.goldBg, bar: T.gold2,   btnBg: '#FEF3C7', btnFg: '#92400E' },
  subject: { border: T.purple, iconBg: '#F5F3FF', bar: T.purple,  btnBg: '#EDE9FE', btnFg: '#5B21B6' },
};

const BADGE_STYLE = {
  live:   { bg: '#FEE2E2', fg: '#991B1B' },
  new:    { bg: T.emBg,    fg: '#065F46' },
  open:   { bg: '#FEF3C7', fg: '#92400E' },
  active: { bg: '#EDE9FE', fg: '#5B21B6' },
};

const RANK_MEDALS = { 1: '🥇', 2: '🥈', 3: '🥉' };

const TABS = [
  { key: 'all',     ar: 'الكل',         en: 'All'        },
  { key: 'weekly',  ar: 'الأسبوعي',     en: 'Weekly'     },
  { key: 'improve', ar: 'تحسين الضعف',  en: 'Improve'    },
  { key: 'daily',   ar: 'اليومي',       en: 'Daily'      },
  { key: 'subject', ar: 'بالمواد',      en: 'By Subject' },
];

const LB_SUBJECTS = [
  { ar: 'العلوم',       en: 'Science' },
  { ar: 'الرياضيات',   en: 'Math'    },
  { ar: 'اللغة العربية', en: 'Arabic' },
];
const LB_PERIODS = [
  { ar: 'هذا الأسبوع', en: 'This Week'  },
  { ar: 'هذا الشهر',   en: 'This Month' },
  { ar: 'الكل',        en: 'All'        },
];

// ─── HELPERS ─────────────────────────────────────────────────────────────────
function formatCountdown(ms, lang) {
  const total = Math.max(0, Math.floor(ms / 1000));
  const h = Math.floor(total / 3600);
  const m = Math.floor((total % 3600) / 60);
  const s = total % 60;
  const prefix = lang === 'ar' ? 'ينتهي بعد' : 'Ends in';
  return `${prefix}: ${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}:${String(s).padStart(2, '0')}`;
}

// ─── SUB-COMPONENTS ──────────────────────────────────────────────────────────


function StreakBar({ days, lang }) {
  return (
    <div style={{
      background: T.goldBg, border: `2px solid rgba(217,119,6,.3)`,
      borderRadius: '20px', padding: '12px 16px', marginBottom: '16px',
      display: 'flex', alignItems: 'center', gap: '14px',
      boxShadow: '0 4px 0 rgba(217,119,6,.2), 0 8px 20px rgba(217,119,6,.08)',
    }}>
      <span style={{ fontSize: '22px', lineHeight: 1 }}>🔥</span>
      <div style={{ flex: 1 }}>
        <div style={{ fontSize: '14px', fontWeight: 700, color: '#92400E', fontFamily: 'Sora, sans-serif' }}>
          {lang === 'ar' ? 'سلسلة التحديات' : 'Challenge Streak'}
        </div>
        <div style={{ fontSize: '12px', color: '#B45309', marginTop: '1px' }}>
          {lang === 'ar' ? 'أكملت تحدياً كل يوم هذا الأسبوع!' : 'You completed a challenge every day this week!'}
        </div>
      </div>
      <div style={{ fontSize: '26px', fontWeight: 800, color: T.gold, fontFamily: 'Sora, sans-serif' }}>
        {days}
      </div>
    </div>
  );
}

function TabBar({ active, onChange, lang }) {
  return (
    <div style={{ display: 'flex', gap: '8px', marginBottom: '16px', flexWrap: 'wrap' }}>
      {TABS.map(({ key, ar, en }) => (
        <button
          key={key}
          onClick={() => onChange(key)}
          style={{
            padding: '7px 16px', borderRadius: '20px',
            fontSize: '13px', fontWeight: 700, cursor: 'pointer',
            border: active === key ? `2px solid ${T.navy}` : '2px solid #E5E7EB',
            background: active === key ? T.navy : '#fff',
            color: active === key ? '#fff' : '#6B7280',
            boxShadow: active === key ? '0 3px 0 rgba(30,58,95,.2)' : '0 2px 0 #E5E7EB',
            transition: 'all .14s', fontFamily: 'Nunito, sans-serif',
          }}
        >
          {lang === 'ar' ? ar : en}
        </button>
      ))}
    </div>
  );
}

function CountdownChip({ endsAt, lang }) {
  const [label, setLabel] = useState(() => formatCountdown(endsAt - Date.now(), lang));

  useEffect(() => {
    const id = setInterval(() => setLabel(formatCountdown(endsAt - Date.now(), lang)), 1000);
    return () => clearInterval(id);
  }, [endsAt, lang]);

  return (
    <div style={{
      display: 'inline-flex', alignItems: 'center', gap: '5px',
      background: '#F3F4F6', borderRadius: '8px',
      padding: '4px 10px', fontSize: '12px', fontWeight: 600,
      color: '#374151', marginBottom: '10px',
    }}>
      <span style={{
        width: '6px', height: '6px', borderRadius: '50%',
        background: T.red, display: 'inline-block',
        animation: 'jahPulse 1.2s infinite',
      }} />
      {label}
    </div>
  );
}

function ChallengeCard({ challenge, onStart, lang }) {
  const isAr = lang === 'ar';
  const STATUS_STYLE = {
    phase1:    { border: '#3B82F6', bg: '#EFF6FF', badge: '#2563EB', label: isAr ? 'بدأت للتو'  : 'Just Started'  },
    phase2:    { border: '#D97706', bg: '#FEF9EE', badge: '#D97706', label: isAr ? 'في التقدم'  : 'In Progress'   },
    phase3:    { border: '#22C55E', bg: '#F0FDF4', badge: '#16A34A', label: isAr ? 'اقتربت!'    : 'Almost There!' },
    completed: { border: '#059669', bg: '#ECFDF5', badge: '#059669', label: isAr ? 'مكتمل'      : 'Completed'     },
  };

  const statusKey = challenge.status === 'completed'
    ? 'completed'
    : (challenge.challenge_status || 'phase1');
  const style = STATUS_STYLE[statusKey] || STATUS_STYLE.phase1;

  const done     = challenge.sessions_done     || 0;
  const required = challenge.sessions_required || 1;
  const pct      = challenge.progress_pct      ?? Math.round((done / required) * 100);
  const isDone   = challenge.status === 'completed';

  const btnLabel = isDone
    ? (isAr ? 'مكتمل ✓' : '✓ Done')
    : done > 0
    ? (isAr ? 'متابعة التحدي ←' : '← Continue')
    : (isAr ? 'ابدأ التحدي ←' : '← Start');

  return (
    <div style={{
      background: style.bg,
      border: '2px solid #E5E7EB',
      borderRight: `5px solid ${style.border}`,
      borderRadius: T.cardRad,
      padding: '18px', cursor: 'pointer',
      boxShadow: '0 5px 0 #E5E7EB',
      transition: 'transform .15s, box-shadow .15s',
    }}
      onMouseEnter={e => { e.currentTarget.style.transform = 'translateY(-5px)'; e.currentTarget.style.boxShadow = '0 5px 0 #D1D5DB, 0 16px 32px rgba(0,0,0,.1)'; }}
      onMouseLeave={e => { e.currentTarget.style.transform = ''; e.currentTarget.style.boxShadow = '0 5px 0 #E5E7EB'; }}
      onMouseDown={e => { e.currentTarget.style.transform = 'translateY(3px)'; e.currentTarget.style.boxShadow = '0 1px 0 #E5E7EB'; }}
      onMouseUp={e => { e.currentTarget.style.transform = 'translateY(-5px)'; e.currentTarget.style.boxShadow = '0 5px 0 #D1D5DB, 0 16px 32px rgba(0,0,0,.1)'; }}
    >
      <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', marginBottom: '10px' }}>
        <div style={{
          width: '44px', height: '44px', borderRadius: '14px',
          background: '#fff', border: `2px solid ${style.border}20`,
          display: 'flex', alignItems: 'center',
          justifyContent: 'center', fontSize: '17px',
        }}>
          🎯
        </div>
        <span style={{
          fontSize: '11px', fontWeight: 700, padding: '3px 9px',
          borderRadius: '100px', background: `${style.badge}18`, color: style.badge,
        }}>
          {style.label}
        </span>
      </div>

      <div style={{ fontSize: '14px', fontWeight: 800, color: '#111827', marginBottom: '3px', fontFamily: 'Sora, sans-serif' }}>
        {challenge.weakness_name}
      </div>
      <div style={{ fontSize: '12px', color: '#6B7280', marginBottom: '12px' }}>
        {isAr ? `الهدف: ${challenge.target_score}%` : `Target: ${challenge.target_score}%`}
      </div>

      <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '10px' }}>
        <div style={{ flex: 1, height: '8px', background: '#F3F4F6', borderRadius: '100px', overflow: 'hidden', boxShadow: 'inset 0 1px 2px rgba(0,0,0,.08)' }}>
          <div style={{ height: '100%', width: `${pct}%`, background: style.border, borderRadius: '100px', transition: 'width .4s' }} />
        </div>
        <div style={{ fontSize: '12px', fontWeight: 700, color: '#6B7280', minWidth: '30px', textAlign: 'left' }}>
          {done}/{required}
        </div>
      </div>

      <div style={{ display: 'flex', gap: '14px' }}>
        {[
          isAr ? `${pct}% مكتمل` : `${pct}% complete`,
          isAr ? `الهدف ${challenge.target_score}%` : `Score ${challenge.target_score}%`,
        ].map(txt => (
          <div key={txt} style={{ display: 'flex', alignItems: 'center', gap: '5px', fontSize: '11px', color: '#6B7280' }}>
            <span style={{ width: '6px', height: '6px', borderRadius: '50%', background: style.border, display: 'inline-block' }} />
            {txt}
          </div>
        ))}
      </div>

      <button
        onClick={() => onStart(challenge)}
        disabled={isDone}
        style={{
          marginTop: '14px', width: '100%', padding: '11px',
          borderRadius: '14px', fontSize: '13px', fontWeight: 900,
          cursor: isDone ? 'default' : 'pointer',
          border: `2px solid ${style.border}`,
          background: `${style.badge}18`,
          color: style.badge,
          boxShadow: `0 4px 0 ${style.border}40`,
          opacity: isDone ? 0.6 : 1,
          transition: 'opacity .14s, transform .1s', fontFamily: 'Nunito, sans-serif',
        }}
        onMouseDown={e => { e.currentTarget.style.transform = 'translateY(3px)'; }}
        onMouseUp={e => { e.currentTarget.style.transform = ''; }}
      >
        {btnLabel}
      </button>
    </div>
  );
}

function Avatar({ letter, colorKey, size = 34 }) {
  const col = AVATAR_COLORS[colorKey] || AVATAR_COLORS.me;
  return (
    <div style={{
      width: size, height: size, borderRadius: '50%', flexShrink: 0,
      background: col.bg, color: col.fg,
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      fontSize: Math.floor(size * 0.41), fontWeight: 700,
    }}>
      {letter}
    </div>
  );
}

function LeaderboardSection({ rows, lang }) {
  const [subject, setSubject] = useState(LB_SUBJECTS[0]);
  const [period, setPeriod]   = useState(LB_PERIODS[0]);

  const top    = rows.filter(r => !r.isMe);
  const meRow  = rows.find(r => r.isMe);
  const myRank = meRow?.rank;
  const topScore = top[0]?.score || 1;

  const needPoints = myRank > 10
    ? (top[9]?.score || 0) + 1 - (meRow?.score || 0)
    : null;

  return (
    <div style={{
      background: '#fff', border: '2px solid #E5E7EB',
      borderRadius: T.cardRad, padding: '20px',
      boxShadow: '0 5px 0 #E5E7EB, 0 10px 28px rgba(0,0,0,.06)',
    }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '14px' }}>
        <div style={{ fontSize: '15px', fontWeight: 800, color: '#111827', fontFamily: 'Sora, sans-serif' }}>
          {lang === 'ar' ? 'لوحة المتصدرين' : 'Leaderboard'}
        </div>
        <select
          value={subject.ar}
          onChange={e => setSubject(LB_SUBJECTS.find(s => s.ar === e.target.value))}
          style={{
            fontSize: '12px', color: '#6B7280', background: '#F9FAFB',
            border: '1px solid #E5E7EB', borderRadius: '8px', padding: '5px 10px',
            cursor: 'pointer', fontFamily: 'Nunito, sans-serif',
          }}
        >
          {LB_SUBJECTS.map(s => <option key={s.ar} value={s.ar}>{lang === 'ar' ? s.ar : s.en}</option>)}
        </select>
      </div>

      <div style={{ display: 'flex', gap: '8px', marginBottom: '16px' }}>
        {LB_PERIODS.map(p => (
          <button
            key={p.ar}
            onClick={() => setPeriod(p)}
            style={{
              fontSize: '12px', fontWeight: 700, padding: '5px 14px',
              borderRadius: '100px', cursor: 'pointer',
              border: period.ar === p.ar ? `2px solid ${T.em}` : '2px solid #E5E7EB',
              background: period.ar === p.ar ? T.em : '#F9FAFB',
              color: period.ar === p.ar ? '#fff' : '#6B7280',
              boxShadow: period.ar === p.ar ? `0 3px 0 ${T.em}40` : '0 2px 0 #E5E7EB',
              transition: 'all .13s', fontFamily: 'Nunito, sans-serif',
            }}
          >
            {lang === 'ar' ? p.ar : p.en}
          </button>
        ))}
      </div>

      {top.map(row => (
        <div key={row.rank} style={{
          display: 'flex', alignItems: 'center', gap: '12px',
          padding: '9px 10px', borderRadius: '14px', marginBottom: '4px',
          transition: 'background .12s', cursor: 'default',
        }}
          onMouseEnter={e => e.currentTarget.style.background = '#F9FAFB'}
          onMouseLeave={e => e.currentTarget.style.background = ''}
        >
          <div style={{ width: '26px', textAlign: 'center', fontSize: row.rank <= 3 ? '17px' : '14px', fontWeight: 700, color: '#6B7280', flexShrink: 0 }}>
            {RANK_MEDALS[row.rank] || row.rank}
          </div>
          <Avatar letter={row.avatar} colorKey={row.avatarColor} />
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ fontSize: '14px', fontWeight: 700, color: '#111827' }}>{row.name}</div>
            <div style={{ fontSize: '11px', color: '#9CA3AF' }}>{lang === 'ar' ? 'الصف 7' : 'Grade 7'}</div>
          </div>
          <div style={{ textAlign: 'left', flexShrink: 0 }}>
            <div style={{ fontSize: '15px', fontWeight: 800, color: '#111827', fontFamily: 'Sora, sans-serif' }}>{row.score}</div>
            <div style={{ fontSize: '10px', color: '#9CA3AF' }}>{lang === 'ar' ? 'نقطة' : 'pts'}</div>
            <div style={{ width: '72px', height: '4px', background: '#F3F4F6', borderRadius: '100px', overflow: 'hidden', marginTop: '3px' }}>
              <div style={{ height: '100%', width: `${Math.round((row.score / topScore) * 100)}%`, background: T.em2, borderRadius: '100px' }} />
            </div>
          </div>
        </div>
      ))}

      {meRow && (
        <>
          <div style={{ borderTop: '1px dashed #E5E7EB', margin: '6px 0', textAlign: 'center', paddingTop: '4px' }}>
            <span style={{ fontSize: '11px', color: '#9CA3AF' }}>· · ·</span>
          </div>
          <div style={{
            display: 'flex', alignItems: 'center', gap: '12px',
            padding: '9px 10px', borderRadius: '14px', marginBottom: '4px',
            background: T.emBg, border: `2px solid ${T.emBord}`,
            boxShadow: `0 3px 0 rgba(5,150,105,.15)`,
          }}>
            <div style={{ width: '26px', textAlign: 'center', fontSize: '14px', fontWeight: 800, color: T.em, flexShrink: 0 }}>
              {meRow.rank}
            </div>
            <Avatar letter={meRow.avatar} colorKey="me" />
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ fontSize: '14px', fontWeight: 700, color: T.em }}>{meRow.name}</div>
              <div style={{ fontSize: '11px', color: '#9CA3AF' }}>{lang === 'ar' ? 'الصف 7' : 'Grade 7'}</div>
            </div>
            <div style={{ textAlign: 'left', flexShrink: 0 }}>
              <div style={{ fontSize: '15px', fontWeight: 800, color: T.em, fontFamily: 'Sora, sans-serif' }}>{meRow.score}</div>
              <div style={{ fontSize: '10px', color: '#9CA3AF' }}>{lang === 'ar' ? 'نقطة' : 'pts'}</div>
              <div style={{ width: '72px', height: '4px', background: '#F3F4F6', borderRadius: '100px', overflow: 'hidden', marginTop: '3px' }}>
                <div style={{ height: '100%', width: `${Math.round((meRow.score / topScore) * 100)}%`, background: T.em2, borderRadius: '100px' }} />
              </div>
            </div>
          </div>
        </>
      )}

      <div style={{
        background: T.emBg, border: `2px solid ${T.emBord}`,
        borderRadius: '16px', padding: '14px 16px',
        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
        marginTop: '14px', boxShadow: '0 3px 0 rgba(5,150,105,.12)',
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
          <div style={{ fontSize: '26px', fontWeight: 800, color: T.em, fontFamily: 'Sora, sans-serif', lineHeight: 1 }}>
            #{myRank}
          </div>
          <div>
            <div style={{ fontSize: '13px', fontWeight: 700, color: '#065F46' }}>{lang === 'ar' ? 'مرتبتك الحالية' : 'Your Rank'}</div>
            <div style={{ fontSize: '11px', color: T.em2, marginTop: '1px' }}>{lang === 'ar' ? 'من أصل 248 طالب' : 'out of 248 students'}</div>
          </div>
        </div>
        {needPoints !== null && needPoints > 0 && (
          <div style={{ fontSize: '12px', fontWeight: 700, color: T.em }}>
            {lang === 'ar' ? `تحتاج ${needPoints} نقطة للمركز العاشر ←` : `← ${needPoints} pts to reach Top 10`}
          </div>
        )}
      </div>
    </div>
  );
}

// ─── MAIN COMPONENT ───────────────────────────────────────────────────────────
export default function Challenges() {
  const navigate = useNavigate();
  const { lang } = useLanguage();
  const [activeTab, setActiveTab] = useState('all');

  const sd        = (() => { try { return JSON.parse(localStorage.getItem('student_data') || '{}'); } catch { return {}; } })();
  const studentId = sd.id;
  const token     = localStorage.getItem('student_token');

  const [challenges,        setChallenges]        = useState([]);
  const [loadingChallenges, setLoadingChallenges] = useState(true);

  useEffect(() => {
    if (!studentId || !token) return;
    setLoadingChallenges(true);
    studentFetch(`${API}/student/challenges/${studentId}`, {
      headers: { Authorization: `Bearer ${token}` },
    })
      .then(r => r ? r.json() : null)
      .then(d => {
        if (!d) return;
        if (d.success) {
          setChallenges([
            ...(d.active    || []),
            ...(d.completed || []),
          ]);
        }
      })
      .catch(() => {})
      .finally(() => setLoadingChallenges(false));
  }, [studentId, token]); // eslint-disable-line react-hooks/exhaustive-deps

  const leaderboard = MOCK_LEADERBOARD;
  const stats = MOCK_STATS;

  const visibleChallenges = activeTab === 'all'
    ? challenges
    : challenges.filter(c => c.type === activeTab);

  function handleStart(challenge) {
    if (!challenge) return;
    if (challenge.unit_id && challenge.unit_name && challenge.subject_name) {
      window.location.href =
        `/student/subject-units?` +
        `subject_id=${challenge.subject_id}` +
        `&subject=${encodeURIComponent(challenge.subject_name)}` +
        `&unit_id=${challenge.unit_id}` +
        `&unit=${encodeURIComponent(challenge.unit_name)}` +
        `&topic=${encodeURIComponent(challenge.weakness_name || '')}`;
    } else {
      window.location.href = '/student/subjects';
    }
  }

  if (loadingChallenges) {
    return (
      <div style={{ direction: lang === 'ar' ? 'rtl' : 'ltr', fontFamily: 'Nunito, sans-serif', minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#9CA3AF', fontSize: '14px' }}>
        {lang === 'ar' ? 'جارٍ التحميل...' : 'Loading...'}
      </div>
    );
  }

  return (
    <div style={{
      direction: lang === 'ar' ? 'rtl' : 'ltr', fontFamily: 'Nunito, sans-serif',
      padding: '0 0 2rem',
      background: 'linear-gradient(160deg,#FFF9F0 0%,#F0FDF9 50%,#F5F0FF 100%)', minHeight: '100vh',
    }}>
      <style>{`
        @keyframes jahPulse {
          0%, 100% { opacity: 1; }
          50% { opacity: 0.3; }
        }
      `}</style>

      <StudentHeader title={lang === 'ar' ? 'التحديات' : 'Challenges'} showBack onBack={() => navigate('/student/dashboard')} lang={lang} />

      <div style={{ padding: '0 20px', marginTop: '16px' }}>
        <StreakBar days={stats.streakDays} lang={lang} />
        <TabBar active={activeTab} onChange={setActiveTab} lang={lang} />

        <div style={{ fontSize: '11px', fontWeight: 700, color: '#9CA3AF', letterSpacing: '.07em', marginBottom: '12px' }}>
          {lang === 'ar' ? 'التحديات النشطة' : 'Active Challenges'}
        </div>

        <div style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(260px, 1fr))',
          gap: '14px', marginBottom: '20px',
        }}>
          {visibleChallenges.map(ch => (
            <ChallengeCard key={ch.id} challenge={ch} onStart={handleStart} lang={lang} />
          ))}
          {visibleChallenges.length === 0 && (
            <div style={{ gridColumn: '1/-1', textAlign: 'center', padding: '40px', color: '#9CA3AF', fontSize: '14px' }}>
              {lang === 'ar' ? 'لا توجد تحديات في هذا التصنيف حالياً' : 'No challenges in this category yet'}
            </div>
          )}
        </div>

        <LeaderboardSection rows={leaderboard} lang={lang} />
      </div>
    </div>
  );
}
